import React, { useEffect, useMemo, useState } from "react";
import { Card } from "antd";
import { Button, Modal, Tooltip, Typography } from "@mui/material";
import styled from "styled-components";
import { isProficiencyBandConfigActive } from "./ProficiencyConfiguration";
import { Close } from '@mui/icons-material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const DOMAIN_LEVELS = [
  { key: "novice", label: "Novice" },
  { key: "early_intermediate", label: "Early Intermediate" },
  { key: "intermediate", label: "Intermediate" },
  { key: "advance", label: "Advance" },
  { key: "advance_high", label: "Advance High" },
];

const CONFIG_DOMAIN_CATEGORIES = [
  { key: "reading", label: "Reading" },
  { key: "writing", label: "Writing" },
  { key: "listening", label: "Listening" },
  { key: "speaking", label: "Speaking" },
  { key: "comprehension", label: "Comprehension" },
  { key: "literacy", label: "Literacy" },
  { key: "vocal", label: "Vocal" },
  { key: "active", label: "Active" },
];

const COMBINED_CATEGORY_KEYS = {
  comprehension: ["listening", "reading"],
  literacy: ["reading", "writing"],
  vocal: ["listening", "speaking"],
  active: ["speaking", "writing"],
};

const getCategoryKeysForMaxPoints = (catKey) => COMBINED_CATEGORY_KEYS[catKey] || [catKey];

const getCategoryTooltip = (key) => {
  const map = {
    comprehension: "Comprehension (Listening + Reading)",
    literacy: "Literacy (Reading + Writing)",
    vocal: "Vocal (Listening + Speaking)",
    active: "Active (Speaking + Writing)",
  };
  return map[key] || null;
};

const ViewCard = styled(Card)`
  max-width: 680px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  margin: 0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: auto;
  &.ant-card .ant-card-body,
  .ant-card .ant-card-body {
    padding: 10px !important;
  }
  @media (max-width: 768px) {
    max-width: 95vw;
  }
`;

const ViewHeader = styled.div`
  padding: 10px !important;
  flex-shrink: 0;
`;

const ViewTablist = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
`;

const ViewTabBtn = styled.button`
  flex: 0 0 auto;
  min-width: 72px;
  padding: 12px 14px;
  border: none;
  background: ${(p) => (p.$active ? "rgba(25, 118, 210, 0.06)" : "transparent")};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (!p.$active ? "rgba(0,0,0,0.6)" : "#1976d2")};
  border-bottom: 2px solid ${(p) => (!p.$active ? "transparent" : "#1976d2")};
  margin-bottom: -1px;
  white-space: nowrap;
`;

const ViewBody = styled.div`
  padding: 10px !important;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ViewRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 120px;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const ViewRowLabel = styled(Typography)`
  font-weight: 500;
  @media (max-width: 600px) {
    grid-column: 1 / -1;
  }
`;

const ReadOnlyValue = styled(Typography)`
  padding: 8px 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 14px;
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const ViewActions = styled.div`
  position: absolute;
  top: 9px;
  right: 10px;
  background-color: red;
  border-radius: 100%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ALL_TABS = [{ key: "overall", label: "Overall" }, ...CONFIG_DOMAIN_CATEGORIES];

const PlacementExamPointSystemViewModal = ({
  open,
  onClose,
  assignment,
  proficiencyRows = [],
  maxExamPoints = 0,
}) => {
  const [activeTab, setActiveTab] = useState("overall");

  const domainConfig = assignment?.domainConfig && typeof assignment.domainConfig === "object"
    ? assignment.domainConfig
    : {};

  const categoryMaxPoint = useMemo(() => {
    const questions = assignment?.questions;
    if (!Array.isArray(questions)) return () => 0;
    return (catKey) =>
      questions
        .filter((s) =>
          getCategoryKeysForMaxPoints(catKey).includes((s.category_type || "").toLowerCase())
        )
        .reduce((sum, s) => sum + (parseFloat(s.max_point) || 0), 0);
  }, [assignment]);

  const bandsActive = isProficiencyBandConfigActive(proficiencyRows, maxExamPoints);

  useEffect(() => {
    if (open) setActiveTab("overall");
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="point-system-view-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1, sm: 2 },
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.5)" },
      }}
    >
      <ViewCard
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <ViewHeader>
          <Typography
            id="point-system-view-modal"
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Overall point system
          </Typography>
          <ViewTablist role="tablist">
            {ALL_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const tip = tab.key === "overall" ? null : getCategoryTooltip(tab.key);
              const inner = (
                <ViewTabBtn
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  $active={isActive}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </ViewTabBtn>
              );
              return (
                <React.Fragment key={tab.key}>
                  {tip ? (
                    <Tooltip title={tip} placement="top">
                      <span style={{ display: "inline-flex" }}>{inner}</span>
                    </Tooltip>
                  ) : (
                    inner
                  )}
                </React.Fragment>
              );
            })}
          </ViewTablist>
          {activeTab !== "overall" && (
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 1.5 }}>
              Max points: {categoryMaxPoint(activeTab)}
            </Typography>
          )}
          {activeTab === "overall" && Number.isFinite(maxExamPoints) && maxExamPoints > 0 && (
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 1.5 }}>
              Exam max points: {maxExamPoints}
            </Typography>
          )}
        </ViewHeader>
        <ViewBody>
          {activeTab === "overall" && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                District proficiency (total score bands)
              </Typography>
              {!Array.isArray(proficiencyRows) || proficiencyRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No saved district proficiency configuration for this exam code. The app uses the
                  default percentage bands (Novice through Advance High) until a valid configuration
                  is saved.
                </Typography>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {!bandsActive && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      These values are stored but are not active for grading until they cover all points
                      from 0 through the exam total with no gaps. Default percentage bands apply until
                      then.
                    </Typography>
                  )}
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 450 }} aria-label="simple table">
                      <TableHead sx={{ background: "rgba(25, 118, 210, 0.06)", borderColor: "rgba(25, 118, 210, 0.2)" }}>
                        <TableRow>
                          <TableCell>Level title</TableCell>
                          <TableCell align="center">Min (points)</TableCell>
                          <TableCell align="center">Max (points)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {proficiencyRows && proficiencyRows.map((row, idx) => (
                          <TableRow
                            key={`${row.title}-${idx}`}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell component="th" scope="row">
                              {String(row.title ?? "").trim() || `Level ${idx + 1}`}
                            </TableCell>
                            <TableCell align="center">{row.min ?? "—"}</TableCell>
                            <TableCell align="center">{row.max ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </>
          )}
          {activeTab !== "overall" && (
            <div role="tabpanel">
              <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 450 }} aria-label="simple table">
                    <TableHead sx={{ background: "rgba(25, 118, 210, 0.06)", borderColor: "rgba(25, 118, 210, 0.2)" }}>
                      <TableRow>
                        <TableCell>Proficiency band</TableCell>
                        <TableCell align="center">From</TableCell>
                        <TableCell align="center">To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DOMAIN_LEVELS && DOMAIN_LEVELS.map((dom) => {
                        const val = domainConfig[activeTab]?.[dom.key];
                        const from = val?.from ?? "";
                        const to = val?.to ?? "";
                        return (
                        <TableRow
                          key={dom.key}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                              {dom.label}
                          </TableCell>
                          <TableCell align="center">
                              {from === "" || from === null || from === undefined ? "—" : String(from)}
                          </TableCell>
                          <TableCell align="center">
                              {to === "" || to === null || to === undefined ? "—" : String(to)}
                          </TableCell>
                        </TableRow>
                        )
                    })}
                    </TableBody>
                  </Table>
                </TableContainer>
            </div>
          )}
          <ViewActions>
              <Close sx={{ color: "#fff", fontSize: 20, cursor: "pointer" }} onClick={onClose} />
          </ViewActions>
        </ViewBody>
      </ViewCard>
    </Modal>
  );
};

export default PlacementExamPointSystemViewModal;
